import { Inject, Injectable, Injector } from '@angular/core';
import {
  AuthService,
  ENVIRONMENT_CONFIG,
  EnvironmentConfig
} from '@ipreo/northstar';
import { PlatformType } from '../types/muni-environment';
import { NotifyService } from './notify.service';

@Injectable()
export class AuthenticatedDocumentService {
  private readonly platform: PlatformType;

  constructor(
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig,
    private readonly injector: Injector,
    private readonly notifyService: NotifyService
  ) {
    this.platform =
      (config.params.platform as PlatformType) || PlatformType.polaris;
  }

  /**
   * Fetches a document with authentication and invokes a callback with the blob.
   * @param url The document URL to fetch
   * @param onSuccess Callback function that receives the blob for further processing
   */
  public fetchAuthenticatedDocument(
    url: string,
    onSuccess: (blob: Blob) => void
  ): void {
    const token = this.getSessionToken();

    if (token) {
      window
        .fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          // Verify the blob has content
          if (blob.size === 0) {
            throw new Error('Empty document received');
          }

          // Invoke the callback with the blob
          onSuccess(blob);
        })
        .catch((error) => {
          const message = 'Error in fetching the document';
          this.notifyService.showErrorMsg(message);
          console.error(message, error);
        });
    } else {
      console.log('No session token found. Unable to fetch the document.');
    }
  }

  // extract session key for sellsider and buyside
  private getSessionToken(): string | null {
    if (this.platform === PlatformType.polaris) {
      const auth = this.injector.get(AuthService);

      return auth?.currentUser.access_token;
    } else if (this.platform === PlatformType.buyside) {
      const a_token = JSON.parse(localStorage.getItem('okta-token-storage'))
        .accessToken;

      return a_token?.accessToken;
    }

    return null;
  }
}
